import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { EscalationBadge, RagBadge, StatusBadge, DelayBadge } from "@/components/widgets";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Lock, Phone } from "lucide-react";

const Field = ({ label, children }) => (
  <div className="space-y-0.5">
    <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</p>
    <div className="text-sm">{children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

const timelineDotClass = (action) => {
  if (action === "escalation") return "bg-[hsl(var(--cx-crimson))]";
  if (action === "status_change") return "bg-[hsl(var(--cx-gold))]";
  return "bg-[hsl(var(--cx-navy))]";
};

/**
 * READ-ONLY complaint detail drawer.
 * Salesforce is the single source of truth — all updates happen there and
 * sync here via the daily dump upload. No edit controls by design.
 */
export const ComplaintDrawer = ({ complaintId, open, onOpenChange }) => {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!complaintId) return;
    setLoading(true);
    api
      .get(`/complaints/${complaintId}`)
      .then((r) => setC(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [complaintId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="complaint-detail-drawer">
        {loading || !c ? (
          <div className="p-6 space-y-3">
            <SheetHeader className="sr-only">
              <SheetTitle>Loading complaint</SheetTitle>
            </SheetHeader>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-40" />
          </div>
        ) : (
          <>
            <SheetHeader className="px-5 pt-5 pb-3 border-b bg-[hsl(var(--cx-surface))] space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <SheetTitle className="font-serif text-xl">{c.complaint_number}</SheetTitle>
                {c.delay_class === "oem" ? <DelayBadge delayClass="oem" /> : <RagBadge rag={c.rag} days={c.days_open} />}
                <span data-testid="complaint-detail-status-badge"><StatusBadge status={c.status} /></span>
                {c.is_open && c.escalation_level ? (
                  <span data-testid="complaint-detail-escalation-badge">
                    <EscalationBadge level={c.escalation_level} showLabel />
                  </span>
                ) : null}
                {c.reopened && (
                  <span className="text-[0.65rem] uppercase tracking-wider text-[hsl(var(--cx-crimson))] font-semibold">Reopened</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {c.customer_name} · {c.outlet_code} {c.outlet_name} · reported {dayjs(c.date_reported).format("DD MMM YYYY")}
              </p>
              <div
                className="flex items-center gap-1.5 text-xs text-muted-foreground bg-white border rounded-md px-2.5 py-1.5 w-fit"
                data-testid="complaint-readonly-notice"
              >
                <Lock size={12} className="text-[hsl(var(--cx-gold))]" />
                Read-only — managed in Salesforce, synced by daily dump upload
              </div>
            </SheetHeader>

            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Mobile">{c.mobile && (<a href={`tel:${c.mobile}`} className="flex items-center gap-1 text-[hsl(var(--cx-navy))] underline-offset-2 hover:underline"><Phone size={12} />{c.mobile}</a>)}</Field>
                <Field label="Function">{c.function}</Field>
                <Field label="Department">{c.department}</Field>
                <Field label="Category">{c.category}</Field>
                <Field label="Assigned SA">{c.assigned_sa}</Field>
                <Field label="Owner">{c.current_owner}</Field>
                <Field label="Priority">{c.priority}</Field>
                <Field label="Source">{c.source}</Field>
                <Field label="Model">{c.model}</Field>
                <Field label="VIN">{c.vin}</Field>
                <Field label="Reg No">{c.registration_no}</Field>
                <Field label="Closure Date">{c.closure_date ? dayjs(c.closure_date).format("DD MMM YYYY") : null}</Field>
                <Field label="Delay Reason">{c.delay_reason}</Field>
              </div>

              {c.description && (
                <div>
                  <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1">Description</p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap border rounded-md p-3 bg-[hsl(var(--cx-surface))]">{c.description}</p>
                </div>
              )}

              {c.resolution_notes && (
                <div>
                  <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1">Resolution Notes</p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap border rounded-md p-3 bg-[hsl(var(--cx-surface))]">{c.resolution_notes}</p>
                </div>
              )}

              <Separator />

              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">
                  Timeline — imports &amp; escalations
                </p>
                <div className="space-y-0" data-testid="complaint-timeline">
                  {(c.activity_log || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                  )}
                  {[...(c.activity_log || [])].reverse().map((a, i) => (
                    <div key={`${a.at}-${a.action}-${i}`} className="flex gap-3 relative pb-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${timelineDotClass(a.action)}`} />
                        <div className="w-px flex-1 bg-border" />
                      </div>
                      <div className="pb-1">
                        <p className="text-sm">{a.detail}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.by} · {dayjs(a.at).format("DD MMM YYYY, HH:mm")} · {(a.action || "").replace(/_/g, " ")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
